import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "relative inline-flex items-center justify-center whitespace-nowrap rounded-md text-base font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary  text-primary-foreground hover:bg-primary/80",
        // "bg-gradient-to-b from-primary to-primaryDark text-primary-foreground hover:bg-primary/80 hover:brightness-110 active:brightness-90 active:shadow-inner shadow-black/10",

        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input/50 bg-background hover:bg-accent hover:text-accent-foreground",

        outline_primary:
          "border border-primary bg-primary text-primary-foreground hover:bg-primary/80",

        secondary:
          "bg-secondary  text-secondary-foreground/80 hover:bg-secondary/80",
        ghost: "hover:bg-accent  hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-sm",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        full: "h-9 px-4 py-2 w-full text-xs",
        // full_sm: "h-10 w-full text-sm",
        // full_md: "h-12 w-full text-base",
        // full_lg: "h-14 w-full text-lg font-semibold",
        full_lg: "h-11 px-4 py-2 w-full",
        wide: "h-10 px-4 w-[140px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  badgeNum?: number;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant,
      size,
      asChild = false,
      badgeNum = 0,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
        {badgeNum > 0 && <span className="badge">{badgeNum}</span>}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
